import { useState, useEffect, useCallback, useMemo } from 'react';
import axios, { AxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sortCategoriesAlphanumeric } from '@shared/lib';

// CSS стили для оптимизации
const styles = {
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '400px'
  },
  loadingSpinner: {
    animation: 'spin 1s linear infinite'
  },
  imageContainer: {
    position: 'relative' as const,
    overflow: 'hidden'
  },
  imageOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)'
  }
};

// CSS анимации
const keyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .woo-cat-image {
    transition: opacity 0.3s ease-in-out;
  }
  
  .woo-cat-image.loading {
    opacity: 0.5;
  }
  
  .woo-cat-image.loaded {
    opacity: 1;
  }
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = keyframes;
  if (!document.head.querySelector('style[data-catalog-optimization]')) {
    styleElement.setAttribute('data-catalog-optimization', 'true');
    document.head.appendChild(styleElement);
  }
}

interface Category {
  id: number;
  name: string;
  slug: string;
  count: number;
  image?: { src: string };
  parent: number;
}

// Хук для ленивой загрузки изображений
const useLazyImage = (src: string | undefined, placeholder: string) => {
  const [imageSrc, setImageSrc] = useState<string>(placeholder);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!src) return;

    setIsLoading(true);
    const img = new Image();
    
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      setImageSrc(placeholder);
      setIsLoading(false);
    };
    
    img.src = src;
  }, [src, placeholder]);

  return { imageSrc, isLoading, isLoaded };
};

// Хук для кэширования данных
const useCachedData = <T,>(key: string, fetchFn: () => Promise<T>, ttl: number = 5 * 60 * 1000) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Проверяем кэш
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < ttl) {
          setData(cachedData);
          setLoading(false);
          return;
        }
      }
      
      // Загружаем новые данные
      const freshData = await fetchFn();
      setData(freshData);
      
      // Сохраняем в кэш
      localStorage.setItem(key, JSON.stringify({
        data: freshData,
        timestamp: Date.now()
      }));
    } catch (err) {
      const error = err as AxiosError;
      setError(error.message || 'Data loading error');
    } finally {
      setLoading(false);
    }
  }, [key, fetchFn, ttl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

const WooCategoriesGrid = () => {
  const [parentCategory, setParentCategory] = useState<Category | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Получаем parent из URL параметров
  const parentId = searchParams.get('cat_parent') ? parseInt(searchParams.get('cat_parent')!) : 0;

  // Прокрутка страницы вверх при монтировании компонента или изменении категории
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [parentId]);

  // Конфигурация API
  const API_CONFIG = {
    baseUrl: 'https://huter.diy/wordpress/wp-json/wc/v3',
    consumerKey: 'ck_b633c1877cf1de3bdbb01117e1fa8215b9b99e43',
    consumerSecret: 'cs_5c69080f905a13fda5aefffb41f6ac3988ab6478',
    placeholderImage: 'https://via.placeholder.com/150',
    defaultProductCat: '0'
  };

  // Функция загрузки родительской категории
  const fetchParentCategory = useCallback(async () => {
    if (parentId === 0) return null;
    
    const parentParams = new URLSearchParams();
    parentParams.append('consumer_key', API_CONFIG.consumerKey);
    parentParams.append('consumer_secret', API_CONFIG.consumerSecret);
    
    const response = await axios.get<Category>(
      `${API_CONFIG.baseUrl}/products/categories/${parentId}?${parentParams.toString()}`
    );
    return response.data;
  }, [parentId]);

  // Функция загрузки категорий
  const fetchCategories = useCallback(async () => {
    const params = new URLSearchParams();
    params.append('parent', parentId.toString());
    params.append('consumer_key', API_CONFIG.consumerKey);
    params.append('consumer_secret', API_CONFIG.consumerSecret);
    params.append('exclude', API_CONFIG.defaultProductCat);
    params.append('hide_empty', 'true');
    
    const response = await axios.get<Category[]>(
      `${API_CONFIG.baseUrl}/products/categories?${params.toString()}`
    );
    
    // Применяем алфавитно-цифровую сортировку к категориям
    return sortCategoriesAlphanumeric(response.data);
  }, [parentId]);

  // Кэшированные данные
  const { data: categories = [], loading: categoriesLoading, error: categoriesError } = useCachedData(
    `categories_${parentId}`,
    fetchCategories,
    10 * 60 * 1000 // 10 минут кэш
  );

  const { data: parentCat, loading: parentLoading } = useCachedData(
    `parent_category_${parentId}`,
    fetchParentCategory,
    10 * 60 * 1000
  );

  // Обновляем родительскую категорию
  useEffect(() => {
    if (parentCat) {
      setParentCategory(parentCat);
    } else {
      setParentCategory(null);
    }
  }, [parentCat]);

  // Предзагрузка изображений для видимых категорий
  const preloadImages = useCallback((cats: Category[]) => {
    const imageUrls = cats
      .map(cat => cat.image?.src)
      .filter(Boolean) as string[];
    
    imageUrls.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (categories && categories.length > 0) {
      preloadImages(categories);
    }
  }, [categories, preloadImages]);

  const handleCategoryClick = async (category: Category) => {
    // Проверяем есть ли подкатегории
    const params = new URLSearchParams();
    params.append('parent', category.id.toString());
    params.append('consumer_key', API_CONFIG.consumerKey);
    params.append('consumer_secret', API_CONFIG.consumerSecret);
    params.append('number', '1');
    
    try {
      const response = await axios.get<Category[]>(
        `${API_CONFIG.baseUrl}/products/categories?${params.toString()}`
      );
      
      if (response.data.length > 0) {
        // Если есть подкатегории - добавляем параметр cat_parent
        searchParams.set('cat_parent', category.id.toString());
        setSearchParams(searchParams);
      } else {
        // Если нет подкатегорий - переходим на страницу категории
        navigate(`/catalog/${category.slug}`);
      }
    } catch {
      // В случае ошибки переходим на страницу категории
      navigate(`/catalog/${category.slug}`);
    }
  };

  const handleBackClick = () => {
    if (parentCategory?.parent) {
      // Переходим к родительской категории
      searchParams.set('cat_parent', parentCategory.parent.toString());
      setSearchParams(searchParams);
    } else {
      // Возвращаемся к корневым категориям
      searchParams.delete('cat_parent');
      setSearchParams(searchParams);
    }
  };

  if (categoriesLoading || parentLoading) {
    return (
      <div className="woo-categories-container container h-screen my-[30px]">
        <div style={styles.loadingContainer}>
          <div className="text-center">
            <div 
              className="rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"
              style={styles.loadingSpinner}
            ></div>
            <p className="text-lg">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="woo-categories-container container h-screen my-[30px]">
        <div className="text-center text-red-500">
          <p>Error: {categoriesError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="woo-categories-container container h-screen my-[30px]">
      {/* Кнопка "Назад" для подкатегорий */}
      {parentId !== 0 && parentCategory && (
        <div className="woo-cat-back">
          <button onClick={handleBackClick} className="woo-back-link">
            ← Back
          </button>
        </div>
      )}

      {/* Сетка категорий */}
      <div className={`woo-categories-grid woo-cat-col-3`}>
        {categories && categories.length > 0 ? (
          categories.map((category) => {
            // Проверяем есть ли подкатегории (асинхронно, поэтому показываем стрелку если count > 0)
            const showArrow = category.count > 0;
            
            return (
              <CategoryItem 
                key={category.id}
                category={category}
                showArrow={showArrow}
                placeholderImage={API_CONFIG.placeholderImage}
                onClick={() => handleCategoryClick(category)}
              />
            );
          })
        ) : (
          <p>Categories not found</p>
        )}
      </div>
    </div>
  );
};

// Оптимизированный компонент категории с ленивой загрузкой изображений
const CategoryItem: React.FC<{
  category: Category;
  showArrow: boolean;
  placeholderImage: string;
  onClick: () => void;
}> = ({ category, showArrow, placeholderImage, onClick }) => {
  const { imageSrc, isLoading, isLoaded } = useLazyImage(category.image?.src, placeholderImage);

  return (
    <div className="woo-cat-item">
      <div
        onClick={onClick}
        className={`woo-cat-link${showArrow ? ' has-children' : ''}`}
        style={{ cursor: 'pointer' }}
      >
        <div
          className={`woo-cat-image ${isLoading ? 'loading' : isLoaded ? 'loaded' : ''}`}
          style={{
            backgroundImage: `url(${imageSrc})`,
            backgroundSize: 'contain',
            ...styles.imageContainer
          }}
        >
          {isLoading && (
            <div style={styles.imageOverlay}>
              <div 
                className="rounded-full h-6 w-6 border-b-2 border-yellow-400"
                style={styles.loadingSpinner}
              ></div>
            </div>
          )}
        </div>
        <h3 className="woo-cat-title">{category.name}</h3>
        <div style={{ width: 'fit-content', margin: 'auto' }}>
          {category.count > 100000 && (
            <span className="woo-cat-count">{category.count} products</span>
          )}
          {showArrow && <span className="woo-cat-arrow"></span>}
        </div>
      </div>
    </div>
  );
};

export default WooCategoriesGrid;